import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, Box, ShieldAlert, Sparkles } from "lucide-react";
interface TrailerUnit {
  id: string;
  title: string;
  category: string;
  categoryVariant: "secondary" | "default";
  image: string;
  isLocalImage?: boolean;
  alt: string;
  exterior: string;
  interior: string;
  power?: string;
  temperature?: string;
  notes: string;
  isCommercialReefer?: boolean;
}
const towableTrailers: TrailerUnit[] = [{
  id: "trailer-6x8",
  title: "6×8 Refrigerated & Freezer Trailer",
  category: "Mobile Towable Unit",
  categoryVariant: "secondary",
  image: "https://fridgefleet.com/wp-content/uploads/2026/09/6x8-FF-trailer.png",
  alt: "6 by 8 mobile refrigerated and freezer trailer",
  exterior: "144″ L × 92″ W × 92″ H",
  interior: "89.5″ L × 65″ W × 62″ H",
  notes: "Power, temperature, equipment, availability, and suitability are confirmed with each quote. Optional generator and shelving may be available for eligible smaller trailers."
}, {
  id: "trailer-7x12",
  title: "7×12 Refrigerated & Freezer Trailer",
  category: "Mobile Towable Unit",
  categoryVariant: "secondary",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796053/img/ChatGPT-Image-Sep-12-2026-06_47_20-PM-1.png",
  isLocalImage: true,
  alt: "7 by 12 mobile refrigerated and freezer trailer",
  exterior: "192″ L × 102″ W × 98″ H",
  interior: "144″ L × 74″ W × 72″ H",
  notes: "Power, temperature, equipment, availability, and suitability are confirmed with each quote. Optional generator and shelving may be available for eligible smaller trailers."
}, {
  id: "trailer-7x16",
  title: "7×16 Refrigerated & Freezer Trailer",
  category: "Mobile Towable Unit",
  categoryVariant: "secondary",
  image: "https://fridgefleet.com/wp-content/uploads/2026/09/7x16-FF-trailer.png",
  alt: "7 by 16 mobile refrigerated and freezer trailer",
  exterior: "240″ L × 102″ W × 92″ H",
  interior: "192″ L × 74″ W × 72″ H",
  notes: "Power, temperature, equipment, availability, and suitability are confirmed with each quote. Optional generator and shelving may be available for eligible smaller trailers."
}];
const commercialReefer: TrailerUnit = {
  id: "trailer-53ft",
  title: "53-Foot Reefer Trailer",
  category: "Full-Size Commercial Semi-Trailer",
  categoryVariant: "default",
  image: "https://fridgefleet.com/wp-content/uploads/2026/09/FF-Reefer-trailer.png",
  alt: "53-foot commercial refrigerated semi trailer",
  exterior: "Typical exterior 53′ L × 102.36″ W × 13′ 6″ H",
  interior: "Typical interior approx. 52′ 2″ L × 97.4″ W × 105.5″ H (manufacturer specs)",
  power: "Individual industrial specification required",
  temperature: "Custom verified based on storage needs",
  notes: "Availability, specifications, temperature requirements, power/fuel needs and suitability for the 53-foot reefer are confirmed individually and are NOT the same as the smaller trailers' specifications — do not apply smaller trailers' electrical, temperature or equipment details to this unit.",
  isCommercialReefer: true
};
export default function TrailerLineup() {
  return <section id="trailerlineupgrid" data-nav="light" className="bg-background text-foreground py-16 sm:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <div className="max-w-3xl mb-12 sm:mb-16">
          <Badge variant="outline" className="mb-3 font-medium">
            Fleet Specifications
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">
            Refrigerated &amp; Freezer Trailer Lineup.
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground">
            Explore mobile refrigerated and freezer trailer options, including 6×8, 7×12, and 7×16 towable trailers plus a 53-foot reefer option. Availability, specifications, requirements, and suitability are confirmed with each quote.
          </p>
        </div>

        {/* Part 1: Three Standard Mobile Towable Trailers */}
        <div className="mb-14">
          <div className="flex items-center gap-3 pb-4 mb-6 border-b border-border">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Mobile Towable Units
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {towableTrailers.map((trailer, i) => <Card key={trailer.id} data-index={i} className="bg-card text-card-foreground border border-border rounded-xl shadow-md flex flex-col justify-between overflow-hidden transition-shadow hover:shadow-lg">
                <div>
                  <div className="relative aspect-16/10 w-full bg-muted/50 border-b border-border p-4 flex items-center justify-center overflow-hidden">
                    {trailer.isLocalImage ? <img src={trailer.image} data-wvc-srcset={`${trailer.image}-wvc-srcset`} data-wvc-sizes={`${trailer.image}-wvc-sizes`} alt={trailer.alt} loading="lazy" className="h-full w-full object-contain" /> : <img src={trailer.image} alt={trailer.alt} loading="lazy" className="h-full w-full object-contain" />}
                  </div>

                  <CardHeader className="pt-6 pb-2">
                    <Badge variant={trailer.categoryVariant} className="mb-2 w-fit">
                      {trailer.category}
                    </Badge>
                    <CardTitle className="text-xl font-bold tracking-tight text-foreground">
                      {trailer.title}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="space-y-4 pt-2 text-sm">
                    {/* Dimensions */}
                    <div className="rounded-lg bg-muted/40 p-3 border border-border/50">
                      <div className="flex items-center gap-1.5 font-medium text-foreground text-xs mb-1.5">
                        <Box className="size-3.5 text-primary" />
                        <span>Dimensions</span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        <strong className="text-foreground">Ext:</strong> {trailer.exterior}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        <strong className="text-foreground">Int:</strong> {trailer.interior}
                      </p>
                    </div>

                    {/* Operational Specs */}
                    <div className="flex items-start gap-2">
                      <Sparkles className="size-4 text-primary shrink-0 mt-0.5" />
                      <span className="text-xs text-muted-foreground">{trailer.notes}</span>
                    </div>
                  </CardContent>
                </div>

                <CardFooter className="pt-4 pb-6">
                  <Button asChild className="w-full">
                    <Link to="/ContactRequestAQuotePage">
                      Request a Quote
                      <ArrowRight className="size-4 ml-1" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>)}
          </div>
        </div>

        {/* Part 2: Separated Commercial 53-Foot Unit */}
        <div>
          <div className="flex items-center gap-3 pb-4 mb-6 border-b border-border">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              53-Foot Reefer Option
            </span>
          </div>

          <Card className="bg-card text-card-foreground border-2 border-primary/20 rounded-xl shadow-lg overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
              {/* Product Visual */}
              <div className="lg:col-span-5 bg-muted/40 p-6 sm:p-8 flex items-center justify-center border-b lg:border-b-0 lg:border-r border-border">
                <img src={commercialReefer.image} alt={commercialReefer.alt} loading="lazy" className="max-h-72 w-full object-contain" />
              </div>

              {/* Details & Specifications */}
              <div className="lg:col-span-7 p-6 sm:p-8 flex flex-col justify-between">
                <div>
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <Badge variant="default" className="text-xs">
                      {commercialReefer.category}
                    </Badge>
                    <span className="text-xs font-semibold text-destructive uppercase tracking-wide flex items-center gap-1">
                      <ShieldAlert className="size-3.5" />
                      Individual Confirmation
                    </span>
                  </div>

                  <h3 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground">
                    {commercialReefer.title}
                  </h3>

                  {/* Dimensions Box */}
                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 p-3.5 rounded-lg bg-muted/40 border border-border/70 text-xs">
                    <div>
                      <span className="font-semibold text-foreground block mb-0.5">Exterior Dimensions</span>
                      <span className="text-muted-foreground">{commercialReefer.exterior}</span>
                    </div>
                    <div>
                      <span className="font-semibold text-foreground block mb-0.5">Interior Dimensions</span>
                      <span className="text-muted-foreground">{commercialReefer.interior}</span>
                    </div>
                  </div>

                  {/* Critical Separate Specification Notice */}
                  <div className="mt-5 rounded-lg border border-amber-500/20 bg-amber-500/5 p-4 text-xs text-foreground/90 leading-relaxed">
                    <p className="font-medium text-amber-900 dark:text-amber-200 mb-1 flex items-center gap-1.5">
                      <ShieldAlert className="size-4 shrink-0 text-amber-600 dark:text-amber-400" />
                      Distinct Unit Specifications &amp; Requirements
                    </p>
                    <p className="text-muted-foreground">{commercialReefer.notes}</p>
                  </div>
                </div>

                <div className="mt-8 pt-4 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-4">
                  <p className="text-xs text-muted-foreground">
                    Potential temporary cold storage for larger commercial needs. Availability, suitability, and operating requirements are confirmed individually.
                  </p>
                  <Button asChild size="lg" className="w-full sm:w-auto shrink-0">
                    <Link to="/ContactRequestAQuotePage">
                      Request a quote
                      <ArrowRight className="size-4 ml-1.5" />
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </section>;
}